import { FC } from 'react';
import classNames from 'classnames';

import { getTimeStamp, getShortDate } from '../../../../lib/time';
import TestClientPill from '../../../../components/TestClientPill';
import Avatar from '../../../../components/Avatar';
import Button from '../../../../components/Button';
import { PlusIcon } from '@heroicons/react/solid';
import { PatientTableDataFragment } from '../../../../../generated/graphql';
import StackedAvatars from '../../../ClientSessions/components/StackedAvatars';
import { useColumnVisibility } from './useColumnVisibility';
import NoText from '../../../../components/NoText';
import ProgramTagChip from '../../../../components/ProgramTag/ProgramTagChip';
import { DEFAULT_TAG_COLOR } from '../../../../lib/colors';

type ArchivedRowProps = {
  isChecked: boolean;
  patient: PatientTableDataFragment;
  patientIndex: number;
  handleUnarchiveClick: (programId: string) => Promise<void>;
  visibleColumns: string[];
  handleRowCheckboxChange?: (
    patient: PatientTableDataFragment,
    patientIndex: number,
    isChecked: boolean,
    isShiftKeyPressed: boolean,
  ) => void;
  setLastSelectedPatientIndex?: React.Dispatch<React.SetStateAction<number>>;
};

const ArchivedRow: FC<ArchivedRowProps> = ({
  isChecked,
  patient,
  handleUnarchiveClick,
  visibleColumns,
}) => {
  const { shouldShowColumn } = useColumnVisibility(visibleColumns);

  return (
    <tr
      key={patient.email}
      className={classNames(
        'group whitespace-nowrap text-green-150',
        isChecked && 'bg-neutral-25',
        !isChecked && 'hover:bg-neutral-25',
      )}
    >
      <td className="w-[3.75rem]" />
      {shouldShowColumn('name') && (
        <td className="py-4 px-0">
          <div className="flex flex-row items-center gap-x-4">
            <Avatar
              imageUrl={patient.profileImageMedia?.url}
              name={patient.firstName ?? ''}
              size="medium"
            />
            <div className="flex flex-col items-start justify-start font-serif text-extra-small">
              <div>{patient.firstName + ' ' + patient.lastName}</div>
              <div className="font-sans text-small-caption">
                {patient.email}
              </div>
            </div>
            {patient.isTestProgram && <TestClientPill />}
          </div>
        </td>
      )}

      {shouldShowColumn('careTeam') && (
        <td className="py-4 px-0">
          <StackedAvatars
            items={
              patient?.assignedProviderUsers?.map((user) => ({
                name: user.name || user.email,
                description: user.email,
                imageUrl: user.profileImageMedia?.url,
              })) ?? []
            }
          />
        </td>
      )}

      {shouldShowColumn('tags') && (
        <td className="max-w-[300px]">
          <div className="flex flex-wrap gap-2">
            {patient.tags?.map((tag) => (
              <ProgramTagChip
                key={tag.id}
                tag={{
                  name: tag.name,
                  color: tag.color ?? DEFAULT_TAG_COLOR,
                }}
              />
            ))}
          </div>
        </td>
      )}

      {shouldShowColumn('programStartDate') && (
        <td className="py-4 px-0">
          {patient.programStartDate ? (
            <span className="text-small-caption">
              {getShortDate(patient.programStartDate)}
            </span>
          ) : (
            <NoText text="—" className="text-caption" />
          )}
        </td>
      )}

      {shouldShowColumn('programEndDate') && (
        <td className="py-4 px-0">
          {patient.programEndDate ? (
            <span className="text-small-caption">
              {getShortDate(patient.programEndDate)}
            </span>
          ) : (
            <NoText text="—" className="text-caption" />
          )}
        </td>
      )}

      {shouldShowColumn('dateAdded') && (
        <td className="py-4 px-0">
          {patient.createdAt ? (
            <span className="text-small-caption">
              {getShortDate(patient.createdAt)}
            </span>
          ) : (
            <NoText text="—" className="text-caption" />
          )}
        </td>
      )}

      {shouldShowColumn('archivedAt') && (
        <td>
          {patient.deactivatedAt && (
            <div className="mt-2 text-small-caption">
              {getTimeStamp(patient?.deactivatedAt, false, true)}
            </div>
          )}
        </td>
      )}

      {shouldShowColumn('actions') && (
        <td>
          {patient.deactivatedAt && (
            <Button
              theme="secondary"
              title="Unarchive"
              size="small"
              iconPosition="left"
              IconComponent={PlusIcon}
              className="rounded-full bg-neutral-50 p-3 hover:bg-neutral-100/75"
              onClick={() => {
                handleUnarchiveClick(patient.programInstanceId);
              }}
            />
          )}
        </td>
      )}
    </tr>
  );
};

export default ArchivedRow;
